// src/storage/progressHelpers.js
//
// Pure, framework-free helpers for persisted study progress (Scope A). JSX-free so
// they unit-test under `node --test`.

// Both readers POOL a pack's quiz items into ONE run (PracticeQuiz / set-text
// QuizView), so a pack has a single quiz-run key in its progress doc.
export const POOLED_QUIZ_KEY = 'quiz';

// Completion percent (0-100, clamped) for a per-pack progress bar.
export function progressPercent(completedCount, total) {
    const t = Number(total) || 0;
    if (t <= 0) return 0;
    const c = Math.max(0, Math.min(Number(completedCount) || 0, t));
    return Math.round((c / t) * 100);
}

// Per-nav-group (unit) COMPLETION over the already-loaded completedSet. `group`
// is a nav group { items:[{id}...] }; a group is `complete` only when it has
// items AND every one is in completedSet. Completion-based (opened = done), not a
// performance/mastery measure. An empty group -> 0/0, 0%, not complete.
export function groupProgress(group, completedSet) {
    const items = (group && group.items) || [];
    const total = items.length;
    const set = completedSet instanceof Set ? completedSet : new Set(completedSet || []);
    let done = 0;
    for (const it of items) if (it && set.has(it.id)) done += 1;
    return { done, total, percent: progressPercent(done, total), complete: total > 0 && done === total };
}

// Pack-level unit rollup over the flat list of nav groups: how many units are
// fully complete out of how many total. Empty groups never count as complete.
export function unitsComplete(groups, completedSet) {
    const list = Array.isArray(groups) ? groups : [];
    const set = completedSet instanceof Set ? completedSet : new Set(completedSet || []);
    let complete = 0;
    for (const g of list) if (groupProgress(g, set).complete) complete += 1;
    return { complete, total: list.length };
}

// Decode a stored quizState blob ({ data:<json string>, updatedAt }) into a
// { answers, submitted } pair. DEFENSIVE: missing / malformed / legacy shapes yield
// empty maps; never throws (bad JSON is swallowed).
export function decodeQuizState(saved) {
    if (!saved || typeof saved !== 'object') return { answers: {}, submitted: {} };
    let parsed = {};
    if (typeof saved.data === 'string') {
        try { parsed = JSON.parse(saved.data) || {}; } catch { parsed = {}; }
    }
    const answers = parsed.answers && typeof parsed.answers === 'object' ? parsed.answers : {};
    const submitted = parsed.submitted && typeof parsed.submitted === 'object' ? parsed.submitted : {};
    return { answers, submitted };
}

// Keep only the entries of `map` whose key is a CURRENT question key (in validKeys) --
// so a resumed session drops answers/submits for questions since removed/reordered out.
export function pruneToKeys(map, validKeys) {
    const keep = validKeys instanceof Set ? validKeys : new Set(validKeys || []);
    const out = {};
    for (const k of Object.keys(map || {})) if (keep.has(k)) out[k] = map[k];
    return out;
}

// Aggregate dashboard stats across an array of progress docs
// ([{ completedItemIds:[...], quizScores:{...} }]): total items completed +
// total quiz runs recorded (one per pack-quiz). Per-section drill runs
// ('drill:<sectionId>' keys, skills-section-drills) are EXCLUDED so the
// "Quizzes Taken" stat keeps its original meaning -- drills reuse the same
// question pool as the pooled quiz, so counting them would double-count.
export function aggregateProgress(docs) {
    let textsRead = 0;
    let quizzesTaken = 0;
    for (const d of docs || []) {
        if (!d) continue;
        if (Array.isArray(d.completedItemIds)) textsRead += d.completedItemIds.length;
        if (d.quizScores && typeof d.quizScores === 'object') {
            quizzesTaken += Object.entries(d.quizScores).filter(([k, r]) => !k.startsWith('drill:') && (r?.answered == null || r.answered >= r.total)).length;
        }
    }
    return { textsRead, quizzesTaken };
}

// Per-pack completion for the library shelf rings and the Continue card
// (library-2-shelf). PURE: `progress` is { completedItemIds } (or a Set / array of
// ids) and `pack` is a catalog pack.
//
// Returns null -- meaning "draw no ring" -- whenever the TOTAL is unknown. That is the
// normal case on the dashboard today: usePacksBridge's adaptPack sets `items: 0`
// because the total-item count is not on the pack doc, and fetching it would mean a
// new Firestore read. A caller that has a real total (the DEV harness, a reader, or a
// future slice that puts counts on the pack) gets a real percentage. Callers should
// fall back to the locally cached per-pack stat (lastVisited.js readPackStats) before
// giving up.
export function packCompletion(progress, pack) {
    const total = Number(pack && pack.items) || 0;
    if (total <= 0) return null;

    let ids;
    if (progress instanceof Set) ids = progress;
    else if (Array.isArray(progress)) ids = new Set(progress);
    else if (progress && Array.isArray(progress.completedItemIds)) ids = new Set(progress.completedItemIds);
    else if (progress && progress.completedSet instanceof Set) ids = progress.completedSet;
    else ids = new Set();

    const done = Math.min(ids.size, total);
    return { done, total, percent: progressPercent(done, total) };
}
